/**
 * AI Assistant Page
 * Interactive chat interface for asking questions about CVEs, patches, and vulnerabilities
 */

import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import hatchAvatar from '../assets/hatch-face.png';
import {
  PaperAirplaneIcon,
  ChatBubbleLeftRightIcon,
  LightBulbIcon,
  ShieldCheckIcon,
  DocumentMagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface SuggestedQuestion {
  text: string;
  icon: typeof LightBulbIcon;
  category: string;
}

const suggestedQuestions: SuggestedQuestion[] = [
  {
    text: 'Which vulnerabilities are actively being exploited right now?',
    icon: ShieldCheckIcon,
    category: 'Active Threats',
  },
  {
    text: 'What software updates should I prioritize for deployment?',
    icon: LightBulbIcon,
    category: 'Patch Priorities',
  },
  {
    text: 'What version of PostgreSQL should I be on?',
    icon: DocumentMagnifyingGlassIcon,
    category: 'Version Guidance',
  },
  {
    text: 'Tell me about memory corruption vulnerabilities in browsers',
    icon: ShieldCheckIcon,
    category: 'Research',
  },
];

export const AIAssistant: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (messageText?: string) => {
    const text = messageText || input;
    if (!text.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: text,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';
      // Send the last 10 messages as history (5 turns) so Hatch has context
      const history = messages.slice(-10).map((m) => ({
        role: m.role,
        content: m.content,
      }));
      const response = await fetch(`${API_BASE_URL}/ai/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: text,
          context: 'chat',
          history,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get AI response');
      }

      const data = await response.json();

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.response || data.message || 'I received your question and am processing it.',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Failed to send message:', error);

      // Show error message
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your request. The AI query endpoint may not be fully configured yet. Please try again later or contact an administrator.',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestedQuestion = (question: string) => {
    handleSendMessage(question);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-2">
          <img src={hatchAvatar} alt="Hatch" className="w-14 h-14 rounded-full object-cover border-2 border-cyber-blue-500 shadow-glow-sm" style={{ objectPosition: '50% 30%' }} />
          <div>
            <h1 className="text-3xl font-bold text-white">Ask Hatch</h1>
            <p className="text-cyber-blue-400 text-sm font-medium">Your AI Security Analyst</p>
          </div>
        </div>
        <p className="text-gray-400 mt-2">
          Ask Hatch anything about CVEs, patch priorities, software versions, and security threats. He knows your entire catalog.
        </p>
      </div>

      {/* Chat Container */}
      <div className="bg-dark-bg-secondary rounded-lg border border-gray-800 flex flex-col h-[600px]">
        {/* Messages Area */}
        <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.length === 0 ? (
            // Welcome State
            <div className="flex flex-col items-center justify-center h-full text-center">
              <img src={hatchAvatar} alt="Hatch" className="w-24 h-24 rounded-full object-cover border-2 border-cyber-blue-500 shadow-glow mb-4" style={{ objectPosition: '50% 30%' }} />
              <h2 className="text-xl font-bold text-white mb-1">Hey, I'm Hatch!</h2>
              <p className="text-gray-400 mb-6 max-w-md">
                Ask me about CVEs, which software to update, active threats, or anything in your security catalog.
              </p>

              {/* Suggested Questions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full max-w-2xl">
                {suggestedQuestions.map((sq, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestedQuestion(sq.text)}
                    className="p-4 bg-dark-bg-tertiary rounded-lg border border-gray-700 hover:border-cyber-blue-500 transition-all text-left group"
                  >
                    <div className="flex items-start gap-3">
                      <sq.icon className="w-5 h-5 text-cyber-blue-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500 mb-1">{sq.category}</p>
                        <p className="text-sm text-gray-300 group-hover:text-white">
                          {sq.text}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            // Messages
            <>
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex gap-3 ${
                    message.role === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  {message.role === 'assistant' && (
                    <img src={hatchAvatar} alt="Hatch" className="w-8 h-8 rounded-full object-cover flex-shrink-0 border border-cyber-blue-500" style={{ objectPosition: '50% 30%' }} />
                  )}

                  <div
                    className={`max-w-[70%] rounded-lg p-4 ${
                      message.role === 'user'
                        ? 'bg-cyber-blue-500 text-white'
                        : 'bg-dark-bg-tertiary text-gray-200 border border-gray-700'
                    }`}
                  >
                    {message.role === 'assistant' ? (
                      <div className="prose prose-invert prose-sm max-w-none
                        prose-p:my-1 prose-ul:my-1 prose-li:my-0
                        prose-strong:text-white prose-a:text-cyber-blue-400 prose-a:no-underline hover:prose-a:underline
                        prose-headings:text-white prose-code:text-cyber-blue-300 prose-code:bg-dark-bg-secondary prose-code:px-1 prose-code:rounded">
                        <ReactMarkdown>{message.content}</ReactMarkdown>
                      </div>
                    ) : (
                      <p className="whitespace-pre-wrap">{message.content}</p>
                    )}
                    <p className="text-xs mt-2 opacity-60">
                      {message.timestamp.toLocaleTimeString()}
                    </p>
                  </div>

                  {message.role === 'user' && (
                    <div className="w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-semibold">You</span>
                    </div>
                  )}
                </div>
              ))}

              {isLoading && (
                <div className="flex gap-3 justify-start">
                  <img src={hatchAvatar} alt="Hatch" className="w-8 h-8 rounded-full object-cover flex-shrink-0 border border-cyber-blue-500" style={{ objectPosition: '50% 30%' }} />
                  <div className="bg-dark-bg-tertiary rounded-lg p-4 border border-gray-700">
                    <div className="flex gap-2">
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input Area */}
        <div className="border-t border-gray-800 p-4">
          <div className="flex gap-3">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask Hatch about CVEs, patch priorities, software versions..."
              disabled={isLoading}
              className="flex-1 px-4 py-3 bg-dark-bg-tertiary border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyber-blue-500 disabled:opacity-50"
            />
            <button
              onClick={() => handleSendMessage()}
              disabled={!input.trim() || isLoading}
              className="px-6 py-3 bg-cyber-blue-500 text-white rounded-lg hover:bg-cyber-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <PaperAirplaneIcon className="w-5 h-5" />
              Send
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </div>

      {/* Info Section */}
      <div className="mt-6 bg-dark-bg-secondary rounded-lg border border-gray-800 p-6">
        <h3 className="text-lg font-semibold text-white mb-3">What can Hatch help with?</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <h4 className="text-cyber-blue-400 font-semibold mb-2">CVE Information</h4>
            <ul className="text-gray-400 space-y-1">
              <li>• Search for specific CVEs</li>
              <li>• Explain vulnerability details</li>
              <li>• Assess CVE severity</li>
            </ul>
          </div>
          <div>
            <h4 className="text-cyber-blue-400 font-semibold mb-2">Patch Management</h4>
            <ul className="text-gray-400 space-y-1">
              <li>• Find patches for software</li>
              <li>• Prioritize updates</li>
              <li>• Check patch status</li>
            </ul>
          </div>
          <div>
            <h4 className="text-cyber-blue-400 font-semibold mb-2">Risk Analysis</h4>
            <ul className="text-gray-400 space-y-1">
              <li>• Evaluate exploit risks</li>
              <li>• Get deployment advice</li>
              <li>• Identify critical threats</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;
